/* Groups — the church's groups for an admin, your own for a member.
 *
 * Straight from the client's handwritten "Add Groups" page: a heading, a
 * Search box and an Add button on the right, then the groups one per row with
 * a chevron. Their sketch labels that chevron "Expand Button"; a row here
 * opens the group's own page instead, because a group has members, moderators
 * and eventually its own feed — more than fits inside an accordion.
 *
 * TERMINOLOGY. Their document calls this list "All groups" and each row a
 * "sub-group" of the church. We say Church and Group — see
 * docs/CLIENT_TERMINOLOGY.md for the mapping and why.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Loader2, Plus, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import {
  useChurch,
  useCreateGroup,
  useGroups,
  useMyGroups,
  useRequestToJoin,
  useMe,
  useChurchAwaitingMe,
  useClaimChurch,
  useMyInvitations,
  useRespondToInvitation,
} from '@/api/queries'
import type { Group, MyGroup } from '@/types/api'

export function GroupsPage() {
  const navigate = useNavigate()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null

  /* TWO AUDIENCES, ONE PAGE.
   *
   * A church admin sees every group in the church and can add one. A member
   * sees the groups they belong to and cannot.
   *
   * This used to be the admin view only. Every admin group endpoint is
   * permission-checked, so a member landed here, got a 403, and read "The
   * groups did not load." — which says the app is broken when the truth was
   * that the page was never meant for them. They were in the group; it just
   * would not show it.
   *
   * Both hooks are declared unconditionally, because hooks must be, and each
   * is `enabled` only for the role that may call it. The disabled one never
   * fires a request. */
  const isAdmin = me?.role === 'church_admin' || me?.role === 'super_admin'

  const church = useChurch(isAdmin ? churchId : null)
  const adminGroups = useGroups(isAdmin ? churchId : null)
  const myGroups = useMyGroups(!isAdmin)

  const [query, setQuery] = useState('')
  const [adding, setAdding] = useState(false)

  const groups = isAdmin ? adminGroups : myGroups
  const rows: Group[] = isAdmin ? adminGroups.data ?? [] : myGroups.data?.items ?? []

  // The admin reads it from the church record; a member gets it on their own
  // groups page, since they may not call the church endpoint at all.
  const churchName = isAdmin ? church.data?.name : myGroups.data?.church_name

  /* Filtering happens here rather than on the server: a church has tens of
     groups, not thousands, and a round trip per keystroke would feel worse
     than instant local matching. */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
    )
  }, [rows, query])

  if (!me) return null

  if (!churchId) {
    return (
      <div className="space-y-s4">
        <header>
          <h1 className="text-2xl font-bold tracking-tight">Groups</h1>
        </header>

        {/* Shown FIRST. Accepting an invitation is exactly how someone with no
            church gets one, so hiding it behind the empty state below would
            strand them. */}
        <ClaimChurchBanner />
        <Invitations />

        <Card className="p-s8 text-center">
          <Users className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No groups yet</p>
          <p className="mt-1 text-base text-ink-2">
            Groups belong to a church. Join one, or accept an invitation when
            somebody sends you here.
          </p>
          {/* This card said "Join one" for weeks with nothing to click behind
              it — the only real way in was being invited. */}
          <Button
            variant="primary"
            className="mt-s4"
            onClick={() => navigate('/find-church')}
          >
            Find a church
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-s4">
      {/* The church name sits directly under the heading — it is the context
          for everything below it, and without it there is nothing on this page
          that says which church you are looking at. */}
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          {isAdmin ? 'Groups' : 'My groups'}
        </h1>
        <p className="mt-1 text-base text-ink-2">{churchName ?? '…'}</p>
      </header>

      <ClaimChurchBanner />
      <Invitations />

      {/* The search bar is its own card. Fused onto the top of the list it
          made the whole thing read as one object, so a group row did not look
          like a separate thing you could open. */}
      <Card className="flex flex-wrap items-center gap-s3 p-s3">
        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search groups"
            aria-label="Search groups"
            className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
          />
        </div>
        {/* Creating a group is an admin action, and the endpoint behind this
            refuses anybody else. A button that can only fail is worse than no
            button. */}
        {isAdmin ? (
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Add
          </Button>
        ) : null}
      </Card>

      {groups.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2
            className="h-5 w-5 animate-spin text-ink-2"
            aria-label="Loading"
          />
        </Card>
      ) : null}

      {!groups.isLoading && !rows.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {isAdmin
            ? 'No groups yet. Add one to organise members within the church.'
            : 'You are not in any groups yet. A group leader can invite you, and the invitation will appear here.'}
        </Card>
      ) : null}

      {/* Searched, found nothing — a different situation from having none,
          and saying "no groups yet" here would read as though they vanished. */}
      {!groups.isLoading && rows.length && !shown.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          No group matches “{query}”.
        </Card>
      ) : null}

      {shown.length ? (
        <ul className="flex flex-col gap-s2">
          {shown.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              churchId={churchId}
              /* An admin sees the church's own listing, which has no
                 membership flags — they can open every group regardless. */
              canOpen={isAdmin || (g as MyGroup).is_member !== false}
            />
          ))}
        </ul>
      ) : null}

      {groups.isError ? (
        <Card className="p-s5 text-center">
          <p className="font-semibold">The groups did not load.</p>
          <Button
            variant="secondary"
            className="mt-s3"
            onClick={() => groups.refetch()}
          >
            Try again
          </Button>
        </Card>
      ) : null}

      {adding ? (
        <AddGroupModal churchId={churchId} onClose={() => setAdding(false)} />
      ) : null}
    </div>
  )
}

/** A church created for you that nobody administers yet.
 *
 *  WHY THIS EXISTS. A church is created with the pastor's mobile, and that
 *  mobile usually already has an account — he was a member first. Signup then
 *  refuses him ("an account already exists"), and signing in showed an ordinary
 *  member app with nothing anywhere saying a church was waiting. The endpoint
 *  that queues the claim had existed since B1 and no client called it, so the
 *  console said "created" and the person named on it could do nothing.
 *
 *  CLAIMING MOVES HIM. One church per person is what every query in this app
 *  assumes, so the confirmation says which church he leaves rather than moving
 *  him out of it quietly.
 */
function ClaimChurchBanner() {
  const awaiting = useChurchAwaitingMe()
  const claim = useClaimChurch()
  const [confirming, setConfirming] = useState(false)

  const church = awaiting.data
  if (!church) return null

  // WAITING IS A STATE, not a moment. This used to depend on claim.isSuccess,
  // which is local and gone on reload — so somebody who registered as the
  // invited admin and came back later saw an ordinary member's app with
  // nothing to say their request existed.
  if (church.awaiting_approval || claim.isSuccess) {
    return (
      <Card className="border-success/30 bg-success-soft">
        <div className="p-s4">
          <h2 className="text-md font-bold">
            {church.name} is waiting to be approved
          </h2>
          <p className="mt-1 text-base text-ink-2">
            You registered as its administrator. A Super Admin reviews it, and
            the church opens for members once they do.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="border-brand/30 bg-brand-softer">
      <div className="p-s4">
        <h2 className="text-md font-bold">
          {church.name} is waiting for you
        </h2>
        <p className="mt-1 text-base text-ink-2">
          {church.city ? `${church.city} · ` : ''}
          You were invited to administer this church.
        </p>

        {confirming ? (
          <div className="mt-s3 rounded bg-surface p-s3">
            <p className="text-base">
              {church.leaving_church_name
                ? `You will leave ${church.leaving_church_name} and administer ${church.name} once a Super Admin approves.`
                : `You will administer ${church.name} once a Super Admin approves.`}
            </p>
            <div className="mt-s3 flex gap-s2">
              <Button
                variant="primary"
                loading={claim.isPending}
                onClick={() => claim.mutate(church.slug)}
              >
                Claim it
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
            {claim.isError ? (
              <p className="mt-s2 text-sm font-semibold text-danger">
                {(claim.error as Error)?.message ?? 'That did not send.'}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="mt-s3">
            <Button variant="primary" onClick={() => setConfirming(true)}>
              Claim it
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}

/** Invitations waiting for this person.
 *
 *  Renders nothing at all when there are none — an empty "no invitations" card
 *  on every visit is noise for the overwhelmingly common case. */
function Invitations() {
  const invitations = useMyInvitations()
  const respond = useRespondToInvitation()

  if (!invitations.data?.length) return null

  return (
    <Card className="border-brand/30 bg-brand-softer">
      <div className="border-b border-line-2 p-s4">
        <h2 className="text-md font-bold">
          {invitations.data.length === 1
            ? 'You have an invitation'
            : `You have ${invitations.data.length} invitations`}
        </h2>
      </div>
      <ul className="divide-y divide-line-2">
        {invitations.data.map((i) => (
          <li
            key={i.id}
            className="flex flex-wrap items-center gap-s3 px-s4 py-s3"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
              <Users className="h-5 w-5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-md font-semibold">
                {i.group_name}
              </span>
              <span className="block truncate text-base text-ink-2">
                {i.church_name}
              </span>
            </span>
            <span className="flex shrink-0 gap-s2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => respond.mutate({ id: i.id, accept: false })}
                disabled={respond.isPending}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => respond.mutate({ id: i.id, accept: true })}
                loading={respond.isPending}
              >
                Join
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function AddGroupModal({
  churchId,
  onClose,
}: {
  churchId: string
  onClose: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const create = useCreateGroup(churchId)

  function submit() {
    const trimmed = name.trim()
    if (!trimmed || create.isPending) return
    create.mutate(
      { name: trimmed, description: description.trim() || null },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="Add group"
      // Closing by accident should not discard a half-typed name.
      guardClose={Boolean(name.trim() || description.trim())}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!name.trim()}
          loading={create.isPending}
        >
          Create group
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          id="group-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Youth Fellowship"
          autoFocus
          required
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
        <Field
          id="group-description"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Sunday evening, ages 15–25"
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
        {create.isError ? (
          <p className="text-base text-danger">
            That did not save. Check the name and try again.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

/** One group row.
 *
 * THREE STATES, because my-groups now returns every group in the church rather
 * than only your own — that is what lets somebody ask to join a group nobody
 * invited them to.
 *
 *   in it            → a link to the group
 *   asked already    → "Requested", disabled; an approver has it
 *   neither          → "Request to join"
 *
 * A row you are not in is deliberately NOT a link: the group detail endpoint
 * refuses non-members, so it would open an error card.
 */
function GroupRow({
  group,
  churchId,
  canOpen,
}: {
  group: Group
  churchId: string | null
  canOpen: boolean
}) {
  const g = group as MyGroup
  const request = useRequestToJoin()

  const body = (
    <>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
        <Users className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-md font-semibold">{group.name}</span>
        <span className="block truncate text-sm text-ink-2">
          {group.member_count.toLocaleString()}{' '}
          {group.member_count === 1 ? 'member' : 'members'}
          {group.description ? ` · ${group.description}` : ''}
        </span>
      </span>
    </>
  )

  if (canOpen) {
    return (
      <li>
        <Link
          to={`/groups/${group.id}`}
          className="cn-card flex items-center gap-s3 px-s4 py-s3 transition-colors hover:bg-hover"
        >
          {body}
          <ChevronRight className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
        </Link>
      </li>
    )
  }

  return (
    <li className="cn-card flex items-center gap-s3 px-s4 py-s3">
      {body}
      {g.has_request ? (
        <span className="shrink-0 rounded-pill bg-surface-2 px-s3 py-s1 text-sm font-semibold text-ink-2">
          Requested
        </span>
      ) : (
        <Button
          variant="secondary"
          loading={request.isPending}
          onClick={() =>
            churchId &&
            request.mutate({ churchId, groupId: group.id })
          }
        >
          Request to join
        </Button>
      )}
    </li>
  )
}
